import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  useMediaQuery,
  Button,
  TextField as MuiTextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from '@material-ui/core';
import { Close as CloseIcon, CameraAlt as CameraIcon } from '@material-ui/icons';
import { BrowserMultiFormatReader } from '@zxing/library';

interface PurchaseItemsGridProps {
  purId?: number;
  onChange: (items: { productId: number; code?: string; qty: number }[]) => void;
}

interface ProductRecord {
  id: number;
  code?: string;
  productName?: string;
  groupId?: number;
}

interface ItemState {
  productId: number;
  code?: string;
  productName?: string;
  designCode?: string;
  qty: number;
}

const step = 10;

const roundUpToNextTen = (value: number): number => {
  if (value <= 0) return 0;
  return Math.ceil(value / 10) * 10;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },

  // Scan bar
  scanBar: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    margin: '12px 0',
    padding: '10px 14px',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
  },
  cameraBtn: {
    minWidth: 44,
    height: 40,
    borderRadius: 8,
    padding: '0 14px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#f1f5f9',
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontWeight: 600,
    fontSize: '0.8rem',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '1.5px dashed #cbd5e1',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: 500,
    gap: 8,
    textAlign: 'center' as const,
  },
  emptyIcon: {
    fontSize: 36,
    marginBottom: 4,
    opacity: 0.5,
  },

  // Summary bar
  summaryBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    borderRadius: '10px 10px 0 0',
    background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
    color: '#f1f5f9',
    fontSize: '0.82rem',
    fontWeight: 600,
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '3px 10px',
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  badgeHighlight: {
    color: '#38bdf8',
    fontWeight: 700,
    fontSize: '0.9rem',
  },

  // Table (desktop)
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  thead: {
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
  },
  th: {
    padding: '10px 14px',
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#475569',
    whiteSpace: 'nowrap' as const,
    textAlign: 'left' as const,
  },
  thCenter: { textAlign: 'center' as const },
  td: {
    padding: '8px 14px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '0.85rem',
    color: '#1e293b',
    verticalAlign: 'middle' as const,
  },
  tdCenter: {
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    padding: '8px 14px',
    borderBottom: '1px solid #f1f5f9',
  },
  designChip: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
    color: '#5b21b6',
    borderRadius: 6,
    padding: '2px 10px',
    fontWeight: 700,
    fontSize: '0.78rem',
    letterSpacing: '0.04em',
    border: '1px solid #c4b5fd',
  },
  codeText: {
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: '0.88rem',
    color: '#0f172a',
  },
  nameText: {
    color: '#64748b',
    fontSize: '0.82rem',
  },

  // Qty controls
  qtyWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  btnMinus: {
    width: 32,
    height: 32,
    borderRadius: 8,
    padding: 0,
    background: '#fee2e2',
    color: '#dc2626',
    fontWeight: 700,
    fontSize: '1.1rem',
    boxShadow: 'none',
    border: '1.5px solid #fca5a5',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  },
  btnPlus: {
    width: 32,
    height: 32,
    borderRadius: 8,
    padding: 0,
    background: '#dcfce7',
    color: '#16a34a',
    fontWeight: 700,
    fontSize: '1.1rem',
    boxShadow: 'none',
    border: '1.5px solid #86efac',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  },

  // Mobile card list
  mobileList: {
    display: 'flex',
    flexDirection: 'column' as const,
    background: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  mobileCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    gap: 8,
  },
  mobileCardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  mobileCardBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },

  // Scanner dialog
  scannerVideoBox: {
    width: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    border: '2px solid #334155',
    background: '#000',
    position: 'relative' as const,
  },
  scannerOverlay: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    height: 80,
    border: '2px solid #38bdf8',
    borderRadius: 6,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
    pointerEvents: 'none' as const,
  },
};

// ─── QtyInput ─────────────────────────────────────────────────────────────────

interface QtyInputProps {
  productId: number;
  qty: number;
  inputValues: Map<number, string>;
  setInputValues: React.Dispatch<React.SetStateAction<Map<number, string>>>;
  updateQty: (productId: number, qty: number) => void;
  width?: number;
}

const QtyInput: React.FC<QtyInputProps> = ({
  productId, qty, inputValues, setInputValues, updateQty, width = 76,
}) => {
  const displayValue = inputValues.has(productId)
    ? inputValues.get(productId)!
    : qty > 0 ? String(qty) : '';

  return (
    <MuiTextField
      style={{ width }}
      variant="outlined"
      size="small"
      type="number"
      inputProps={{
        min: 0,
        style: { textAlign: 'center', fontWeight: 600, fontSize: '0.9rem', padding: '6px 4px' },
      }}
      value={displayValue}
      onChange={(e) => {
        const val = e.target.value;
        setInputValues((prev) => {
          const next = new Map(prev);
          next.set(productId, val);
          return next;
        });
      }}
      onBlur={(e) => {
        const raw = parseInt(e.target.value || '0', 10) || 0;
        const rounded = roundUpToNextTen(raw);
        updateQty(productId, rounded);
        setInputValues((prev) => {
          const next = new Map(prev);
          next.delete(productId);
          return next;
        });
      }}
    />
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const PurchaseItemsGrid: React.FC<PurchaseItemsGridProps> = ({ purId, onChange }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  const [items, setItems] = useState<ItemState[]>([]);
  const [inputValues, setInputValues] = useState<Map<number, string>>(new Map());
  const [scanCode, setScanCode] = useState('');
  const scanInputRef = useRef<HTMLInputElement | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);

  // ── unchanged helpers ──────────────────────────────────────────────────────

  const focusScanInput = () => {
    if (scanInputRef.current) scanInputRef.current.focus();
  };

  const totalQty = items.reduce((sum, it) => sum + (it.qty || 0), 0);
  const totalProducts = items.filter((it) => (it.qty || 0) > 0).length;

  const syncChange = (updated: ItemState[]) => {
    setItems(updated);
    onChange(
      updated
        .filter((it) => it.qty > 0)
        .map((it) => ({ productId: it.productId, code: it.code, qty: it.qty }))
    );
  };

  const updateQty = (productId: number, newQty: number) => {
    if (newQty < 0) newQty = 0;
    newQty = Math.floor(newQty / step) * step;
    setItems((prev) => {
      const updated = prev.map((it) =>
        it.productId === productId ? { ...it, qty: newQty } : it
      );
      onChange(
        updated
          .filter((it) => it.qty > 0)
          .map((it) => ({ productId: it.productId, code: it.code, qty: it.qty }))
      );
      return updated;
    });
  };

  const handlePlus = (productId: number) => {
    const current = items.find((it) => it.productId === productId)?.qty || 0;
    updateQty(productId, current + step);
  };

  const handleMinus = (productId: number) => {
    const current = items.find((it) => it.productId === productId)?.qty || 0;
    updateQty(productId, current - step);
  };

  const handleScan = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;

      const existing = items.find((it) => it.code?.toLowerCase() === trimmed.toLowerCase());
      if (existing) {
        updateQty(existing.productId, (existing.qty || 0) + step);
        setScanCode('');
        focusScanInput();
        return;
      }

      try {
        const result: any = await dataProvider.getList('products', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: { code: trimmed },
        });
        if (result.data && result.data.length > 0) {
          const p: ProductRecord = result.data[0];
          let designCodeValue = '';
          if (p.groupId) {
            try {
              const productGroup: any = await dataProvider.getOne('product-groups', { id: p.groupId });
              designCodeValue = productGroup.data?.designCode || '';
            } catch (e) {
              console.error('Failed to fetch design code from product groups:', e);
            }
          }
          const newItem: ItemState = {
            productId: p.id,
            code: p.code,
            productName: p.productName,
            designCode: designCodeValue,
            qty: step,
          };
          const updated = [...items, newItem];
          syncChange(updated);
        } else {
          notify('Item not found', { type: 'warning' });
        }
      } catch (e) {
        notify('Item not found', { type: 'warning' });
      }

      setScanCode('');
      focusScanInput();
    },
    [dataProvider, items, notify]
  );

  useEffect(() => {
    const loadExisting = async () => {
      if (!purId) return;
      try {
        const existing: any = await dataProvider.getList('pur-items', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'ASC' },
          filter: { purId },
        });
        const rows = existing.data || [];
        const loaded: ItemState[] = [];
        for (const row of rows) {
          try {
            const product: any = await dataProvider.getOne('products', { id: row.productId });
            const p = product.data;
            let designCodeValue = '';
            if (p.groupId) {
              try {
                const productGroup: any = await dataProvider.getOne('product-groups', { id: p.groupId });
                designCodeValue = productGroup.data?.designCode || '';
              } catch (e) {
                console.error('Failed to fetch design code from product groups:', e);
              }
            }
            loaded.push({
              productId: row.productId,
              code: p.code,
              productName: p.productName,
              designCode: designCodeValue,
              qty: row.qty || 0,
            });
          } catch {
            // ignore product load errors
          }
        }
        syncChange(loaded);
      } catch {
        // ignore preload errors
      }
    };
    loadExisting();
  }, [dataProvider, purId]);

  useEffect(() => {
    focusScanInput();
  }, []);

  const startScanner = useCallback(async () => {
    try {
      setScannerError('');
      setIsScanning(true);
      if (!codeReaderRef.current) {
        codeReaderRef.current = new BrowserMultiFormatReader();
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => { videoRef.current?.play(); };
      }
      if (videoRef.current) {
        codeReaderRef.current.decodeFromVideoElement(videoRef.current).then((result) => {
          if (result) {
            const scannedText = result.getText();
            if (scannedText) {
              handleScan(scannedText);
              stopScanner();
            }
          }
        }).catch((error) => {
          console.error('Decode error:', error);
          setScannerError('Failed to decode barcode. Please try again.');
          setIsScanning(false);
        });
      }
    } catch (error) {
      console.error('Scanner error:', error);
      setScannerError('Failed to access camera. Please ensure camera permissions are granted.');
      setIsScanning(false);
    }
  }, [handleScan]);

  const stopScanner = useCallback(() => {
    if (codeReaderRef.current) codeReaderRef.current.reset();
    setIsScanning(false);
    setScannerOpen(false);
  }, []);

  useEffect(() => {
    if (scannerOpen && !isScanning) startScanner();
    return () => { if (codeReaderRef.current) codeReaderRef.current.reset(); };
  }, [scannerOpen, isScanning, startScanner]);

  const handleScanKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); handleScan(scanCode); }
  };

  // ── Shared sub-components ─────────────────────────────────────────────────

  const SummaryBar = () => (
    <div style={styles.summaryBar}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span style={styles.badge}>
          Products &nbsp;<span style={styles.badgeHighlight}>{totalProducts}</span>
        </span>
        <span style={styles.badge}>
          Total Qty &nbsp;<span style={styles.badgeHighlight}>{totalQty}</span>
        </span>
      </div>
      <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Purchase Items</span>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={styles.root}>

      {/* Scan bar */}
      <div style={styles.scanBar}>
        <MuiTextField
          label="Scan Item Code"
          variant="outlined"
          size="small"
          fullWidth
          inputRef={scanInputRef}
          value={scanCode}
          onChange={(e) => setScanCode(e.target.value)}
          onKeyDown={handleScanKeyDown}
        />
        <button
          style={styles.cameraBtn}
          onClick={() => setScannerOpen(true)}
          title="Open Camera Scanner"
        >
          <CameraIcon style={{ fontSize: 20 }} />
        </button>
      </div>

      {/* Empty state */}
      {items.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📷</div>
          <div>No items scanned yet.</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            Scan a barcode or type a code above and press Enter.
          </div>
        </div>
      ) : isSmall ? (
        // ── MOBILE ──────────────────────────────────────────────────────────
        <div style={styles.root}>
          <SummaryBar />
          <div style={styles.mobileList}>
            {items.map((item, idx) => (
              <div
                key={item.productId}
                style={{
                  ...styles.mobileCard,
                  background: item.qty > 0 ? '#f0fdf4' : idx % 2 === 0 ? '#fff' : '#fafafa',
                  borderLeft: item.qty > 0 ? '3px solid #22c55e' : '3px solid transparent',
                }}
              >
                {/* Top: design chip + item info */}
                <div style={styles.mobileCardTop}>
                  <div>
                    {item.designCode && (
                      <span style={styles.designChip}>{item.designCode}</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flex: 1, minWidth: 0 }}>
                    <div style={styles.codeText}>{item.code}</div>
                    {item.productName && (
                      <div style={{ ...styles.nameText, marginTop: 2 }}>{item.productName}</div>
                    )}
                  </div>
                </div>
                {/* Bottom: qty controls */}
                <div style={styles.mobileCardBottom}>
                  <button style={styles.btnMinus} onClick={() => handleMinus(item.productId)}>−</button>
                  <QtyInput
                    productId={item.productId}
                    qty={item.qty}
                    inputValues={inputValues}
                    setInputValues={setInputValues}
                    updateQty={updateQty}
                    width={68}
                  />
                  <button style={styles.btnPlus} onClick={() => handlePlus(item.productId)}>+</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // ── DESKTOP ─────────────────────────────────────────────────────────
        <div style={styles.root}>
          <SummaryBar />
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th}>Design</th>
                  <th style={styles.th}>Item Code</th>
                  <th style={styles.th}>Item Name</th>
                  <th style={{ ...styles.th, ...styles.thCenter, width: 200 }}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={item.productId}
                    style={{
                      background: item.qty > 0 ? '#f0fdf4' : idx % 2 === 0 ? '#fff' : '#fafafa',
                      borderLeft: item.qty > 0 ? '3px solid #22c55e' : '3px solid transparent',
                      transition: 'background 0.2s ease',
                    }}
                  >
                    <td style={styles.td}>
                      {item.designCode
                        ? <span style={styles.designChip}>{item.designCode}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.codeText}>{item.code}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.nameText}>{item.productName}</span>
                    </td>
                    <td style={styles.tdCenter}>
                      <div style={styles.qtyWrapper}>
                        <button style={styles.btnMinus} onClick={() => handleMinus(item.productId)}>−</button>
                        <QtyInput
                          productId={item.productId}
                          qty={item.qty}
                          inputValues={inputValues}
                          setInputValues={setInputValues}
                          updateQty={updateQty}
                          width={76}
                        />
                        <button style={styles.btnPlus} onClick={() => handlePlus(item.productId)}>+</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Barcode Scanner Dialog (unchanged logic) ── */}
      <Dialog
        open={scannerOpen}
        onClose={stopScanner}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmall}
        PaperProps={{
          style: {
            borderRadius: isSmall ? 0 : 16,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle disableTypography style={{
          background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
          color: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CameraIcon style={{ fontSize: 22, color: '#38bdf8' }} />
            <Typography variant="h6" style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700 }}>
              Scan Barcode
            </Typography>
          </div>
          <IconButton onClick={stopScanner} size="small" style={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: 20, background: '#0f172a' }}>
          {scannerError ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={300}
              padding={2}
            >
              <div style={{ fontSize: 40, marginBottom: 12 }}>📵</div>
              <Typography style={{ color: '#f87171', fontWeight: 600, textAlign: 'center', marginBottom: 8 }}>
                {scannerError}
              </Typography>
              <Typography variant="body2" style={{ color: '#94a3b8', textAlign: 'center', marginBottom: 20 }}>
                Please allow camera access in your browser settings and refresh the page.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
                style={{ borderRadius: 8, textTransform: 'none', fontWeight: 600 }}
              >
                Refresh Page
              </Button>
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              style={{ gap: 12 }}
            >
              <Typography variant="body2" style={{ color: '#94a3b8', marginBottom: 8, textAlign: 'center' }}>
                Position the barcode within the blue frame to scan
              </Typography>
              <div style={{
                ...styles.scannerVideoBox,
                height: isSmall ? '55vh' : 320,
              }}>
                <video
                  ref={videoRef}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  autoPlay
                  playsInline
                />
                {/* targeting overlay */}
                <div style={styles.scannerOverlay} />
                {/* corner accents */}
                {[
                  { top: 12, left: 12, borderTop: '3px solid #38bdf8', borderLeft: '3px solid #38bdf8' },
                  { top: 12, right: 12, borderTop: '3px solid #38bdf8', borderRight: '3px solid #38bdf8' },
                  { bottom: 12, left: 12, borderBottom: '3px solid #38bdf8', borderLeft: '3px solid #38bdf8' },
                  { bottom: 12, right: 12, borderBottom: '3px solid #38bdf8', borderRight: '3px solid #38bdf8' },
                ].map((s, i) => (
                  <div key={i} style={{ position: 'absolute', width: 20, height: 20, borderRadius: 2, ...s }} />
                ))}
              </div>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ background: '#0f172a', padding: '12px 20px' }}>
          <Button
            onClick={stopScanner}
            style={{
              borderRadius: 8,
              textTransform: 'none',
              fontWeight: 600,
              color: '#94a3b8',
              border: '1px solid #334155',
              padding: '6px 20px',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};